import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, TrendingUp, TrendingDown, Plus, Pencil, Trash2, Check, AlertTriangle, Receipt, Repeat, HardHat, ArrowDownCircle, ArrowUpCircle, Paperclip, FileCheck2, Upload, Download } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const PAYMENT_METHODS = ['Bonifico', 'RiBa', 'RID/SDD', 'Contanti', 'Assegno', 'Carta', 'F24', 'Altro'];
const REC_CATEGORIES = [
  { v: 'mutuo', l: 'Mutuo' }, { v: 'finanziamento', l: 'Finanziamento' }, { v: 'abbonamento', l: 'Abbonamento' },
  { v: 'software', l: 'Software' }, { v: 'noleggio', l: 'Noleggio' }, { v: 'assicurazione', l: 'Assicurazione' },
  { v: 'utenza', l: 'Utenza' }, { v: 'altro', l: 'Altro' },
];
const NOW_Y = new Date().getFullYear();
const YEARS = [NOW_Y - 2, NOW_Y - 1, NOW_Y, NOW_Y + 1];
const eur = (n) => '€ ' + (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyInvoice = () => ({ direction: 'ricevuta', has_invoice: true, counterpart_name: '', invoice_number: '', imponibile: '', iva_percent: 22, amount: '', invoice_date: todayISO(), due_date: '', payment_method: 'Bonifico', site_id: '__none__', notes: '', status: 'da_pagare' });
const emptyRec = () => ({ description: '', category: 'mutuo', amount: '', day_of_month: 5, start_date: todayISO(), end_date: '', payment_method: 'Bonifico', notes: '', active: true });
const emptyLabor = () => ({ employee_name: '', employee_id: '__none__', site_id: '__none__', month: todayISO().slice(0, 7), amount: '', gross_amount: '', source: 'manual', description: '' });
const emptyInst = () => ({ description: '', amount: '', due_date: todayISO(), payment_method: 'Bonifico', status: 'da_pagare', expected_date: '', payment_date: null });

export default function ScadenziarioPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse');
  const [year, setYear] = useState(NOW_Y);
  const [vatMode, setVatMode] = useState('con'); // 'con' | 'senza'
  const [selectedSite, setSelectedSite] = useState('__all__');
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMeta = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([api.get('/sites'), api.get('/employees/list/simple').catch(() => ({ data: [] }))]);
      setSites(s.data || []); setEmployees(e.data || []);
    } catch (err) { console.error(err); }
  }, []);
  const loadSummary = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/finance/summary', { params: { year } }); setSummary(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const siteName = (id) => sites.find(s => s.id === id)?.name || '';
  const gFull = summary?.general;
  const g = gFull ? (vatMode === 'senza' ? { ...gFull, ...(gFull.netto || {}) } : gFull) : null;
  const curSite = selectedSite !== '__all__' ? (summary?.sites?.find(s => s.site_id === selectedSite) || null) : null;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="scadenziario-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Wallet size={24} /> Scadenziario Finanziario</h1>
          <p className="text-sm text-slate-500">Fatture, uscite ricorrenti, costi cantiere e situazione attivo/negativo dell'azienda.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="flex-1 min-w-[150px] sm:flex-none sm:w-64" data-testid="site-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Generale (tutta l'azienda)</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm" data-testid="vat-toggle">
            <button onClick={() => setVatMode('con')} className={`px-3 py-1.5 ${vatMode === 'con' ? 'bg-[#1a4b8c] text-white' : 'bg-white text-slate-600'}`} data-testid="vat-con">Con IVA</button>
            <button onClick={() => setVatMode('senza')} className={`px-3 py-1.5 ${vatMode === 'senza' ? 'bg-[#1a4b8c] text-white' : 'bg-white text-slate-600'}`} data-testid="vat-senza">Senza IVA</button>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 sm:w-28" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Riepilogo generale */}
      {selectedSite === '__all__' && g && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
          <SummaryCard label="Incassato" value={g.incassato} icon={ArrowDownCircle} tone="green" testid="card-incassato" />
          <SummaryCard label="Da incassare" value={g.da_incassare} icon={TrendingUp} tone="blue" testid="card-da-incassare" />
          <SummaryCard label="Debiti (da pagare)" value={g.debiti} icon={ArrowUpCircle} tone="red" testid="card-debiti" />
          <SummaryCard label="Ricorrenti da pagare" value={g.ricorrenti_da_pagare} icon={Repeat} tone="amber" testid="card-ricorrenti" />
          <SummaryCard label="Uscite totali" value={g.uscite_totali} icon={TrendingDown} tone="slate" testid="card-uscite" />
          <SummaryCard label="Saldo previsto" value={g.saldo_proiezione} icon={Wallet} tone={g.saldo_proiezione >= 0 ? 'green' : 'red'} highlight testid="card-saldo" />
        </div>
      )}

      {/* Salute cantieri (clic per aprire il dettaglio) */}
      {selectedSite === '__all__' && summary?.sites?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><HardHat size={16} /> Andamento cantieri ({summary.sites.length}) <span className="text-xs font-normal text-slate-400">— tocca un cantiere per il dettaglio</span></h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.sites.map(s => (
                <button key={s.site_id} onClick={() => setSelectedSite(s.site_id)} className={`text-left rounded-lg border p-3 transition-colors hover:shadow-md ${s.stato === 'bene' ? 'border-green-200 bg-green-50/50 hover:bg-green-50' : 'border-red-200 bg-red-50/50 hover:bg-red-50'}`} data-testid={`site-health-${s.site_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-slate-800 truncate">{s.site_name}</p>
                    <Badge className={s.stato === 'bene' ? 'bg-green-600' : 'bg-red-600'}>{s.stato === 'bene' ? 'In attivo' : 'In perdita'}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <p>Ricavi: <span className="font-medium text-slate-700">{eur(s.ricavi)}</span></p>
                    <p>Costi: <span className="font-medium text-slate-700">{eur(s.costi)}</span> <span className="text-slate-400">(manodopera {eur(s.manodopera)}, materiali {eur(s.materiali)})</span></p>
                    <p className={`font-bold ${s.margine >= 0 ? 'text-green-700' : 'text-red-700'}`}>Margine: {eur(s.margine)}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vista SINGOLO CANTIERE */}
      {selectedSite !== '__all__' && (
        <SiteSummary site={curSite} name={siteName(selectedSite)} onBack={() => setSelectedSite('__all__')} />
      )}

      {/* Sezioni operative (tutto a colpo d'occhio, senza tab) */}
      {selectedSite === '__all__' && (
        <>
          <SectionTitle icon={Repeat} title="Uscite ricorrenti (generali)" />
          <RecurringSection year={year} canManage={canManage} onChange={loadSummary} />
        </>
      )}

      <SectionTitle icon={Receipt} title={selectedSite === '__all__' ? "Fatture e movimenti (tutta l'azienda)" : `Fatture e movimenti — ${siteName(selectedSite)}`} />
      <InvoicesSection year={year} sites={sites} canManage={canManage} onChange={loadSummary} siteName={siteName} vatMode={vatMode} siteFilter={selectedSite === '__all__' ? null : selectedSite} />

      <SectionTitle icon={HardHat} title={selectedSite === '__all__' ? "Costi manodopera per cantiere" : `Costi manodopera — ${siteName(selectedSite)}`} />
      <LaborSection year={year} sites={sites} employees={employees} canManage={canManage} onChange={loadSummary} siteName={siteName} siteFilter={selectedSite === '__all__' ? null : selectedSite} />
    </div>
  );
}

function SiteSummary({ site, name, onBack }) {
  const s = site || { ricavi: 0, costi: 0, manodopera: 0, materiali: 0, margine: 0, stato: 'bene' };
  return (
    <div className="space-y-3" data-testid="site-summary">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={s.margine >= 0 ? 'bg-green-600' : 'bg-red-600'}>{s.margine >= 0 ? 'In attivo' : 'In perdita'}</Badge>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><HardHat size={18} /> {name}</h2>
        </div>
        <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-general">← Torna al generale</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="site-summary-cards">
        <SummaryCard label="Ricavi" value={s.ricavi} icon={ArrowDownCircle} tone="green" testid="site-card-ricavi" />
        <SummaryCard label="Costi" value={s.costi} icon={ArrowUpCircle} tone="red" testid="site-card-costi" />
        <SummaryCard label="Manodopera" value={s.manodopera} icon={HardHat} tone="amber" testid="site-card-manodopera" />
        <SummaryCard label="Materiali" value={s.materiali} icon={Receipt} tone="slate" testid="site-card-materiali" />
        <SummaryCard label="Margine" value={s.margine} icon={Wallet} tone={s.margine >= 0 ? 'green' : 'red'} highlight testid="site-card-margine" />
      </div>
      {!site && <p className="text-xs text-slate-400">Nessun movimento registrato per questo cantiere nell'anno selezionato. Fatture e costi aggiunti qui sotto compariranno nel riepilogo.</p>}
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="h-8 w-1 rounded bg-[#1a4b8c]" />
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Icon size={18} /> {title}</h2>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, highlight, testid }) {
  const tones = {
    green: 'text-green-700', red: 'text-red-700', blue: 'text-blue-700', amber: 'text-amber-700', slate: 'text-slate-700',
  };
  return (
    <div className={`rounded-lg border p-3 ${highlight ? (value >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50') : 'border-slate-200 bg-white'}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Icon size={14} /> {label}</div>
      <p className={`text-lg font-bold ${tones[tone] || 'text-slate-800'}`}>{eur(value)}</p>
    </div>
  );
}

/* ============ FATTURE ============ */
function InvoicesSection({ year, sites, canManage, onChange, siteName, vatMode, siteFilter }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const onImportFile = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/finance/import-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      setDialog({
        ...emptyInvoice(),
        direction: d.direction || 'ricevuta',
        counterpart_name: d.counterpart_name || '',
        invoice_number: d.invoice_number || '',
        invoice_date: d.invoice_date || todayISO(),
        due_date: d.due_date || '',
        imponibile: d.imponibile ? String(d.imponibile) : '',
        iva_percent: d.iva_percent || 22,
        amount: d.amount ? String(d.amount) : '',
        status: (d.direction === 'emessa') ? 'da_incassare' : 'da_pagare',
        notes: d.source === 'ai' ? 'Importato da scontrino/PDF (AI)' : (d.source === 'xml' ? 'Importato da fattura elettronica XML' : ''),
      });
    } catch (e) { alert(e.response?.data?.detail || 'Errore lettura documento'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  const load = useCallback(async () => {
    const params = {};
    if (filter === 'emesse') params.direction = 'emessa';
    if (filter === 'ricevute') params.direction = 'ricevuta';
    if (filter === 'senza') params.direction = undefined;
    if (siteFilter) params.site_id = siteFilter;
    const r = await api.get('/invoices', { params });
    let list = (r.data || []).filter(i => !year || (i.invoice_date || i.due_date || '').slice(0, 4) === String(year));
    if (filter === 'senza') list = list.filter(i => i.has_invoice === false);
    setItems(list);
  }, [filter, year, siteFilter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setDialog({ ...emptyInvoice() });
  const openEdit = (i) => setDialog({ ...i, site_id: i.site_id || '__none__', imponibile: String(i.imponibile ?? ''), amount: String(i.amount ?? ''), iva_percent: i.iva_percent ?? 22, has_invoice: i.has_invoice !== false });
  const setDir = (dir) => setDialog(d => ({ ...d, direction: dir, status: dir === 'emessa' ? (d.status === 'pagata' || d.status === 'incassata' ? 'incassata' : 'da_incassare') : (d.status === 'incassata' || d.status === 'pagata' ? 'pagata' : 'da_pagare') }));

  // IVA: aggiorna totale da imponibile+iva
  const recalcTot = (imp, iva) => { const i = parseFloat(imp), v = parseFloat(iva); if (isNaN(i)) return ''; return (i * (1 + (isNaN(v) ? 0 : v) / 100)).toFixed(2); };
  const recalcImp = (tot, iva) => { const t = parseFloat(tot), v = parseFloat(iva); if (isNaN(t)) return ''; return (t / (1 + (isNaN(v) ? 0 : v) / 100)).toFixed(2); };

  const save = async () => {
    if (!dialog.counterpart_name.trim() || (!dialog.amount && !dialog.imponibile)) { alert('Fornitore/Cliente e importo obbligatori'); return; }
    setSaving(true);
    try {
      const payload = {
        ...dialog,
        imponibile: dialog.imponibile === '' ? undefined : Number(dialog.imponibile),
        amount: dialog.amount === '' ? undefined : Number(dialog.amount),
        iva_percent: Number(dialog.iva_percent) || 0,
        site_id: dialog.site_id === '__none__' ? null : dialog.site_id,
      };
      let saved;
      if (dialog.id) saved = (await api.put(`/invoices/${dialog.id}`, payload)).data;
      else saved = (await api.post('/invoices', payload)).data;
      setDialog({ ...saved, site_id: saved.site_id || '__none__', imponibile: String(saved.imponibile ?? ''), amount: String(saved.amount ?? ''), has_invoice: saved.has_invoice !== false, _justSaved: true });
      load(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  const uploadFile = async (kind, file) => {
    if (!dialog?.id || !file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/invoices/${dialog.id}/upload?kind=${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDialog(d => ({ ...d, [kind === 'contabile' ? 'payment_receipt_url' : 'attachment_url']: r.data.file_url }));
      load();
    } catch (e) { alert('Errore upload'); }
  };
  // upload inline dalla riga tabella (senza aprire il dialog)
  const pickAndUpload = (invoiceId, kind) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.pdf,.jpg,.jpeg,.png';
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return;
      const fd = new FormData(); fd.append('file', f);
      try { await api.post(`/invoices/${invoiceId}/upload?kind=${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); load(); }
      catch (e) { alert('Errore upload'); }
    };
    inp.click();
  };
  const togglePaid = async (i) => {
    const emessa = i.direction === 'emessa';
    const paid = i.status === 'pagata' || i.status === 'incassata';
    const newStatus = paid ? (emessa ? 'da_incassare' : 'da_pagare') : (emessa ? 'incassata' : 'pagata');
    await api.put(`/invoices/${i.id}`, { status: newStatus, payment_date: paid ? null : todayISO() });
    load(); onChange();
  };
  const del = async (i) => { if (!window.confirm('Eliminare la fattura?')) return; await api.delete(`/invoices/${i.id}`); load(); onChange(); };

  const shownAmount = (i) => vatMode === 'senza' ? (i.imponibile ?? i.amount) : i.amount;
  const dueBadge = (i) => {
    const paid = i.status === 'pagata' || i.status === 'incassata';
    if (!i.due_date) return <span className="text-slate-400 text-xs">-</span>;
    if (paid) return <span className="text-slate-400 text-xs">{fmt(i.due_date)}</span>;
    const days = Math.round((new Date(i.due_date) - new Date(todayISO())) / 86400000);
    if (days < 0) return (
      <div className="flex flex-col gap-0.5">
        <Badge className="bg-red-600 w-fit">Scaduta {Math.abs(days)}g</Badge>
        <span className="text-slate-500 text-xs">{fmt(i.due_date)}</span>
      </div>
    );
    if (days <= 20) return (
      <div className="flex flex-col gap-0.5">
        <Badge className="bg-amber-500 w-fit">tra {days}g</Badge>
        <span className="text-slate-500 text-xs">{fmt(i.due_date)}</span>
      </div>
    );
    return <span className="text-slate-500 text-xs">{fmt(i.due_date)}</span>;
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le fatture/movimenti</SelectItem>
              <SelectItem value="emesse">Emesse (clienti — da incassare)</SelectItem>
              <SelectItem value="ricevute">Ricevute (fornitori — da pagare)</SelectItem>
              <SelectItem value="senza">Senza fattura (in attesa)</SelectItem>
            </SelectContent>
          </Select>
          {canManage && (
            <div className="flex items-center gap-2">
              <input ref={importRef} type="file" accept=".xml,.p7m,.pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => onImportFile(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => importRef.current?.click()} disabled={importing} data-testid="import-doc-btn"><Upload size={16} className="mr-1" /> {importing ? 'Leggo...' : 'Importa fattura/scontrino'}</Button>
              <Button onClick={openNew} data-testid="new-invoice-btn"><Plus size={16} className="mr-1" /> Nuova fattura / movimento</Button>
            </div>
          )}
        </div>
        {/* Mobile: lista a schede */}
        <div className="md:hidden space-y-2">
          {items.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna fattura</p>}
          {items.map(i => {
            const paid = i.status === 'pagata' || i.status === 'incassata';
            return (
              <div key={i.id} className="border border-slate-100 rounded-lg p-3" data-testid={`invoice-card-${i.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 flex-wrap mb-1">
                      {i.direction === 'emessa' ? <Badge variant="outline" className="text-blue-700 border-blue-200">Emessa</Badge> : <Badge variant="outline" className="text-orange-700 border-orange-200">Ricevuta</Badge>}
                      {i.has_invoice === false && <Badge variant="outline" className="text-amber-600 border-amber-200">no fatt.</Badge>}
                    </div>
                    <p className="font-medium text-slate-800 truncate">{i.counterpart_name || i.supplier_name}</p>
                    <p className="text-xs text-slate-500">{fmt(i.invoice_date)}{i.invoice_number ? ` · N.${i.invoice_number}` : ''} · {i.payment_method}</p>
                    {i.site_name && <p className="text-xs text-slate-400">{i.site_name}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold">{eur(shownAmount(i))}</p>
                    <div className="mt-1">{dueBadge(i)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox checked={paid} onCheckedChange={() => canManage && togglePaid(i)} disabled={!canManage} data-testid={`m-toggle-paid-${i.id}`} />
                    <span className="text-xs text-slate-500">{paid ? (i.payment_date ? `${i.direction === 'emessa' ? 'Incassato' : 'Pagato'} il ${fmt(i.payment_date)}` : (i.direction === 'emessa' ? 'Incassato' : 'Pagato')) : (i.direction === 'emessa' ? 'Da incassare' : 'Da pagare')}</span>
                  </label>
                  <div className="flex items-center gap-2.5">
                    {i.attachment_url && <a href={`${BACKEND}${i.attachment_url}`} target="_blank" rel="noreferrer" title="Scarica fattura" data-testid={`m-att-fattura-${i.id}`}><Download size={16} className="text-[#1a4b8c]" /></a>}
                    {canManage && <button onClick={() => pickAndUpload(i.id, 'fattura')} title="Carica fattura" data-testid={`m-up-fattura-${i.id}`}><Paperclip size={16} className="text-slate-500" /></button>}
                    {i.payment_receipt_url && <a href={`${BACKEND}${i.payment_receipt_url}`} target="_blank" rel="noreferrer" title="Scarica contabile" data-testid={`m-att-contabile-${i.id}`}><Download size={16} className="text-green-600" /></a>}
                    {canManage && <button onClick={() => pickAndUpload(i.id, 'contabile')} title="Carica contabile" data-testid={`m-up-contabile-${i.id}`}><FileCheck2 size={16} className="text-slate-500" /></button>}
                    {canManage && <button onClick={() => openEdit(i)} data-testid={`m-edit-invoice-${i.id}`}><Pencil size={16} className="text-slate-500" /></button>}
                    {canManage && <button onClick={() => del(i)} data-testid={`m-del-invoice-${i.id}`}><Trash2 size={16} className="text-red-500" /></button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b">
              <th className="py-2 pr-2">Tipo</th><th className="pr-2">Fornitore/Cliente</th><th className="pr-2">N.</th><th className="pr-2">Data</th><th className="pr-2 text-right">Importo {vatMode === 'senza' ? '(imp.)' : '(IVA incl.)'}</th><th className="pr-2">Scadenza</th><th className="pr-2">Metodo</th><th className="pr-2">Cantiere</th><th className="pr-2">Allegati (PDF)</th><th className="pr-2">Pagato / Incassato</th><th></th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-slate-400">Nessuna fattura</td></tr>}
              {items.map(i => (
                <tr key={i.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`invoice-row-${i.id}`}>
                  <td className="py-2 pr-2">{i.direction === 'emessa' ? <Badge variant="outline" className="text-blue-700 border-blue-200">Emessa</Badge> : <Badge variant="outline" className="text-orange-700 border-orange-200">Ricevuta</Badge>}{i.has_invoice === false && <Badge variant="outline" className="ml-1 text-amber-600 border-amber-200">no fatt.</Badge>}</td>
                  <td className="pr-2 font-medium text-slate-800">{i.counterpart_name || i.supplier_name}</td>
                  <td className="pr-2 text-slate-500">{i.invoice_number || '-'}</td>
                  <td className="pr-2 text-slate-500">{fmt(i.invoice_date)}</td>
                  <td className="pr-2 text-right font-semibold">{eur(shownAmount(i))}</td>
                  <td className="pr-2">{dueBadge(i)}</td>
                  <td className="pr-2 text-slate-500">{i.payment_method}</td>
                  <td className="pr-2 text-slate-500">{i.site_name || '-'}</td>
                  <td className="pr-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-400 w-12">Fattura</span>
                        {i.attachment_url && <a href={`${BACKEND}${i.attachment_url}`} target="_blank" rel="noreferrer" title="Scarica fattura" data-testid={`att-fattura-${i.id}`}><Download size={14} className="text-[#1a4b8c]" /></a>}
                        {canManage && <button onClick={() => pickAndUpload(i.id, 'fattura')} title={i.attachment_url ? 'Sostituisci fattura' : 'Carica fattura'} data-testid={`up-fattura-${i.id}`}><Upload size={14} className={i.attachment_url ? 'text-slate-300 hover:text-slate-500' : 'text-slate-500 hover:text-[#1a4b8c]'} /></button>}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-400 w-12">Contab.</span>
                        {i.payment_receipt_url && <a href={`${BACKEND}${i.payment_receipt_url}`} target="_blank" rel="noreferrer" title="Scarica contabile" data-testid={`att-contabile-${i.id}`}><Download size={14} className="text-green-600" /></a>}
                        {canManage && <button onClick={() => pickAndUpload(i.id, 'contabile')} title={i.payment_receipt_url ? 'Sostituisci contabile' : 'Carica contabile'} data-testid={`up-contabile-${i.id}`}><Upload size={14} className={i.payment_receipt_url ? 'text-slate-300 hover:text-slate-500' : 'text-slate-500 hover:text-green-600'} /></button>}
                      </div>
                    </div>
                  </td>
                  <td className="pr-2">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={i.status === 'pagata' || i.status === 'incassata'} onCheckedChange={() => canManage && togglePaid(i)} disabled={!canManage} data-testid={`toggle-paid-${i.id}`} />
                      <span className="text-xs text-slate-500">{i.status === 'pagata' || i.status === 'incassata' ? (i.payment_date ? `${i.direction === 'emessa' ? 'Incassato' : 'Pagato'} il ${fmt(i.payment_date)}` : (i.direction === 'emessa' ? 'Incassato' : 'Pagato')) : (i.direction === 'emessa' ? 'Da incassare' : 'Da pagare')}</span>
                    </label>
                  </td>
                  <td className="pr-1 whitespace-nowrap">{canManage && <><Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-invoice-${i.id}`}><Pencil size={14} /></Button><Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(i)} data-testid={`del-invoice-${i.id}`}><Trash2 size={14} /></Button></>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{dialog?.id ? 'Modifica' : 'Nuova'} fattura / movimento</DialogTitle><DialogDescription>Registra una fattura emessa (cliente) o ricevuta (fornitore), oppure una transazione senza fattura.</DialogDescription></DialogHeader>
            {dialog && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setDir('ricevuta')} className={`flex-1 rounded-lg border p-2 text-sm font-medium ${dialog.direction === 'ricevuta' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-slate-200'}`} data-testid="dir-ricevuta">Ricevuta (da pagare)</button>
                  <button type="button" onClick={() => setDir('emessa')} className={`flex-1 rounded-lg border p-2 text-sm font-medium ${dialog.direction === 'emessa' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200'}`} data-testid="dir-emessa">Emessa (da incassare)</button>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer bg-amber-50 border border-amber-200 rounded p-2"><Checkbox checked={dialog.has_invoice === false} onCheckedChange={(v) => setDialog({ ...dialog, has_invoice: !v })} data-testid="inv-no-invoice" /> Transazione <strong>senza fattura</strong> (la fattura verrà inserita quando ricevuta)</label>
                <div className="space-y-1"><Label>{dialog.direction === 'emessa' ? 'Cliente' : 'Fornitore'} *</Label><Input value={dialog.counterpart_name} onChange={(e) => setDialog({ ...dialog, counterpart_name: e.target.value })} data-testid="inv-counterpart" /></div>
                <div className="space-y-1"><Label>N. fattura {dialog.has_invoice === false && <span className="text-slate-400">(opz. — nessuna fattura)</span>}</Label><Input value={dialog.invoice_number} onChange={(e) => setDialog({ ...dialog, invoice_number: e.target.value })} data-testid="inv-number" /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1"><Label>Imponibile €</Label><Input type="number" step="0.01" value={dialog.imponibile} onChange={(e) => setDialog({ ...dialog, imponibile: e.target.value, amount: recalcTot(e.target.value, dialog.iva_percent) })} data-testid="inv-imponibile" /></div>
                  <div className="space-y-1"><Label>IVA %</Label><Input type="number" step="1" value={dialog.iva_percent} onChange={(e) => setDialog({ ...dialog, iva_percent: e.target.value, amount: recalcTot(dialog.imponibile, e.target.value) })} data-testid="inv-iva" /></div>
                  <div className="space-y-1"><Label>Totale € *</Label><Input type="number" step="0.01" value={dialog.amount} onChange={(e) => setDialog({ ...dialog, amount: e.target.value, imponibile: recalcImp(e.target.value, dialog.iva_percent) })} data-testid="inv-amount" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Data fattura/movimento</Label><Input type="date" value={dialog.invoice_date} onChange={(e) => setDialog({ ...dialog, invoice_date: e.target.value })} data-testid="inv-date" /></div>
                  <div className="space-y-1"><Label>Data scadenza</Label><Input type="date" value={dialog.due_date} onChange={(e) => setDialog({ ...dialog, due_date: e.target.value })} data-testid="inv-due" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Metodo pagamento</Label>
                    <Select value={dialog.payment_method || 'Bonifico'} onValueChange={(v) => setDialog({ ...dialog, payment_method: v })}><SelectTrigger data-testid="inv-method"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="space-y-1"><Label>Cantiere (opz.)</Label>
                    <Select value={dialog.site_id} onValueChange={(v) => setDialog({ ...dialog, site_id: v })}><SelectTrigger data-testid="inv-site"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={dialog.status === 'pagata' || dialog.status === 'incassata'} onCheckedChange={(v) => setDialog({ ...dialog, status: v ? (dialog.direction === 'emessa' ? 'incassata' : 'pagata') : (dialog.direction === 'emessa' ? 'da_incassare' : 'da_pagare'), payment_date: v ? todayISO() : null })} data-testid="inv-paid" /> {dialog.direction === 'emessa' ? 'Già incassata' : 'Già pagata'}{(dialog.status === 'pagata' || dialog.status === 'incassata') && dialog.payment_date ? ` il ${fmt(dialog.payment_date)}` : ''}</label>
                <div className="space-y-1"><Label>Note</Label><Textarea value={dialog.notes} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} data-testid="inv-notes" /></div>

                {/* Allegati (dopo il salvataggio) */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Allegati {!dialog.id && <span className="font-normal text-slate-400">(salva prima per allegare)</span>}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`flex items-center gap-2 text-sm border rounded-lg p-2 cursor-pointer ${dialog.attachment_url ? 'border-[#1a4b8c] text-[#1a4b8c]' : 'border-slate-200 text-slate-500'} ${!dialog.id ? 'opacity-50 pointer-events-none' : ''}`} data-testid="upload-fattura-label">
                        <Paperclip size={15} /> {dialog.attachment_url ? 'Fattura caricata' : 'Allega fattura'}
                        <input type="file" className="hidden" onChange={(e) => uploadFile('fattura', e.target.files?.[0])} data-testid="upload-fattura" />
                      </label>
                    </div>
                    <div>
                      <label className={`flex items-center gap-2 text-sm border rounded-lg p-2 cursor-pointer ${dialog.payment_receipt_url ? 'border-green-600 text-green-700' : 'border-slate-200 text-slate-500'} ${!dialog.id ? 'opacity-50 pointer-events-none' : ''}`} data-testid="upload-contabile-label">
                        <FileCheck2 size={15} /> {dialog.payment_receipt_url ? 'Contabile caricata' : 'Allega contabile'}
                        <input type="file" className="hidden" onChange={(e) => uploadFile('contabile', e.target.files?.[0])} data-testid="upload-contabile" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setDialog(null)}>Chiudi</Button><Button onClick={save} disabled={saving} data-testid="save-invoice-btn">{saving ? 'Salvo...' : (dialog?.id ? 'Aggiorna' : 'Salva')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

/* ============ USCITE RICORRENTI ============ */
function RecurringSection({ year, canManage, onChange }) {
  const [recs, setRecs] = useState([]);
  const [instances, setInstances] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [instDialog, setInstDialog] = useState(null);
  const [savingInst, setSavingInst] = useState(false);

  const load = useCallback(async () => {
    const [r, inst] = await Promise.all([api.get('/finance/recurring'), api.get('/finance/recurring-instances', { params: { year } })]);
    setRecs(r.data || []); setInstances(inst.data || []);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!dialog.description.trim() || !dialog.amount) { alert('Descrizione e importo obbligatori'); return; }
    setSaving(true);
    try {
      const payload = { ...dialog, amount: Number(dialog.amount), day_of_month: Number(dialog.day_of_month) };
      if (dialog.id) await api.put(`/finance/recurring/${dialog.id}`, payload);
      else await api.post('/finance/recurring', payload);
      setDialog(null); load(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  const del = async (r) => { if (!window.confirm('Eliminare l\'uscita ricorrente? Le rate future non pagate verranno rimosse.')) return; await api.delete(`/finance/recurring/${r.id}`); load(); onChange(); };
  const toggleInst = async (inst) => {
    const paid = inst.status === 'pagata';
    await api.put(`/finance/recurring-instances/${inst.id}`, { status: paid ? 'da_pagare' : 'pagata', payment_date: paid ? null : todayISO() });
    load(); onChange();
  };
  const saveInst = async () => {
    if (!instDialog.description.trim() || !instDialog.amount || !instDialog.due_date) { alert('Descrizione, importo e scadenza obbligatori'); return; }
    setSavingInst(true);
    try {
      const payload = { ...instDialog, amount: Number(instDialog.amount) };
      if (instDialog.id) await api.put(`/finance/recurring-instances/${instDialog.id}`, payload);
      else await api.post('/finance/recurring-instances', payload);
      setInstDialog(null); load(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setSavingInst(false); }
  };
  const delInst = async (inst) => { if (!window.confirm('Eliminare questa rata?')) return; await api.delete(`/finance/recurring-instances/${inst.id}`); load(); onChange(); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-slate-700">Uscite ricorrenti (generali)</h3>{canManage && <Button size="sm" onClick={() => setDialog(emptyRec())} data-testid="new-recurring-btn"><Plus size={15} className="mr-1" /> Aggiungi</Button>}</div>
          <div className="space-y-2">
            {recs.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna uscita ricorrente</p>}
            {recs.map(r => (
              <div key={r.id} className="flex items-center gap-2 border border-slate-100 rounded-lg p-2" data-testid={`recurring-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0"><span className="font-medium text-slate-800 truncate">{r.description}</span> {!r.active && <Badge variant="outline" className="text-slate-400 shrink-0">sospesa</Badge>}</div>
                  <p className="text-xs text-slate-500">{REC_CATEGORIES.find(c => c.v === r.category)?.l || r.category} · {eur(r.amount)}/mese · giorno {r.day_of_month}</p>
                </div>
                {canManage && <><Button size="icon" variant="ghost" onClick={() => setDialog({ ...r, amount: String(r.amount), end_date: r.end_date || '' })} data-testid={`edit-recurring-${r.id}`}><Pencil size={14} /></Button><Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(r)} data-testid={`del-recurring-${r.id}`}><Trash2 size={14} /></Button></>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Rate {year}</h3>
            {canManage && <Button size="sm" variant="outline" onClick={() => setInstDialog(emptyInst())} data-testid="new-instance-btn"><Plus size={15} className="mr-1" /> Aggiungi rata</Button>}
          </div>
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {instances.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna rata</p>}
            {instances.map(inst => {
              const paid = inst.status === 'pagata';
              const days = Math.round((new Date(inst.due_date) - new Date(todayISO())) / 86400000);
              return (
                <div key={inst.id} className="flex items-center gap-2 border-b border-slate-50 py-1.5" data-testid={`instance-row-${inst.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 min-w-0"><span className="text-sm text-slate-700 truncate">{inst.description}</span> {inst.manual && <Badge variant="outline" className="text-slate-400 text-[10px] shrink-0">manuale</Badge>}</div>
                    <p className="text-xs text-slate-500">Scad. {fmt(inst.due_date)} · {eur(inst.amount)}{inst.payment_method ? ` · ${inst.payment_method}` : ''}</p>
                    {paid ? (
                      <p className="text-xs text-green-600">✓ Soldi presi{inst.payment_date ? ` il ${fmt(inst.payment_date)}` : ''}</p>
                    ) : inst.expected_date ? (
                      <p className="text-xs text-blue-600">Previsto il {fmt(inst.expected_date)}</p>
                    ) : null}
                  </div>
                  {!paid && days < 0 && <Badge className="bg-red-600">Scaduta</Badge>}
                  {!paid && days >= 0 && days <= 20 && <Badge className="bg-amber-500">{days}g</Badge>}
                  {canManage && <>
                    <Button size="sm" variant={paid ? 'ghost' : 'outline'} onClick={() => toggleInst(inst)} data-testid={`toggle-instance-${inst.id}`}><Check size={13} className="mr-1" />{paid ? 'Pagata' : 'Paga'}</Button>
                    <Button size="icon" variant="ghost" onClick={() => setInstDialog({ ...inst, amount: String(inst.amount), expected_date: inst.expected_date || '', due_date: inst.due_date || '' })} data-testid={`edit-instance-${inst.id}`}><Pencil size={13} /></Button>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => delInst(inst)} data-testid={`del-instance-${inst.id}`}><Trash2 size={13} /></Button>
                  </>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog?.id ? 'Modifica' : 'Nuova'} uscita ricorrente</DialogTitle><DialogDescription>Genererà automaticamente una rata mensile in scadenza.</DialogDescription></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Descrizione *</Label><Input value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} placeholder="es. Mutuo capannone" data-testid="rec-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Categoria</Label><Select value={dialog.category} onValueChange={(v) => setDialog({ ...dialog, category: v })}><SelectTrigger data-testid="rec-cat"><SelectValue /></SelectTrigger><SelectContent>{REC_CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1"><Label>Importo (€) *</Label><Input type="number" step="0.01" value={dialog.amount} onChange={(e) => setDialog({ ...dialog, amount: e.target.value })} data-testid="rec-amount" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Giorno</Label><Input type="number" min={1} max={31} value={dialog.day_of_month} onChange={(e) => setDialog({ ...dialog, day_of_month: e.target.value })} data-testid="rec-day" /></div>
                <div className="space-y-1"><Label>Dal</Label><Input type="date" value={dialog.start_date} onChange={(e) => setDialog({ ...dialog, start_date: e.target.value })} data-testid="rec-start" /></div>
                <div className="space-y-1"><Label>Al (opz.)</Label><Input type="date" value={dialog.end_date} onChange={(e) => setDialog({ ...dialog, end_date: e.target.value })} data-testid="rec-end" /></div>
              </div>
              <div className="space-y-1"><Label>Metodo pagamento</Label><Select value={dialog.payment_method || 'Bonifico'} onValueChange={(v) => setDialog({ ...dialog, payment_method: v })}><SelectTrigger data-testid="rec-method"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={dialog.active} onCheckedChange={(v) => setDialog({ ...dialog, active: !!v })} data-testid="rec-active" /> Attiva</label>
            </div>
          )}
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button><Button onClick={save} disabled={saving} data-testid="save-recurring-btn">{saving ? 'Salvo...' : 'Salva'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!instDialog} onOpenChange={(o) => !o && setInstDialog(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{instDialog?.id ? 'Modifica' : 'Nuova'} rata / scadenza</DialogTitle><DialogDescription>Aggiungi una singola rata con la sua scadenza e indica se i soldi sono già stati presi o quando lo saranno.</DialogDescription></DialogHeader>
          {instDialog && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Descrizione *</Label><Input value={instDialog.description} onChange={(e) => setInstDialog({ ...instDialog, description: e.target.value })} placeholder="es. Rata mutuo Marzo" data-testid="inst-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Importo (€) *</Label><Input type="number" step="0.01" value={instDialog.amount} onChange={(e) => setInstDialog({ ...instDialog, amount: e.target.value })} data-testid="inst-amount" /></div>
                <div className="space-y-1"><Label>Scadenza *</Label><Input type="date" value={instDialog.due_date} onChange={(e) => setInstDialog({ ...instDialog, due_date: e.target.value })} data-testid="inst-due" /></div>
              </div>
              <div className="space-y-1"><Label>Metodo pagamento</Label><Select value={instDialog.payment_method || 'Bonifico'} onValueChange={(v) => setInstDialog({ ...instDialog, payment_method: v })}><SelectTrigger data-testid="inst-method"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer bg-green-50 border border-green-200 rounded p-2"><Checkbox checked={instDialog.status === 'pagata'} onCheckedChange={(v) => setInstDialog({ ...instDialog, status: v ? 'pagata' : 'da_pagare', payment_date: v ? todayISO() : null })} data-testid="inst-paid" /> Soldi già presi{instDialog.status === 'pagata' && instDialog.payment_date ? ` il ${fmt(instDialog.payment_date)}` : ''}</label>
              {instDialog.status !== 'pagata' && (
                <div className="space-y-1"><Label>Quando prenderai i soldi (previsione, opz.)</Label><Input type="date" value={instDialog.expected_date} onChange={(e) => setInstDialog({ ...instDialog, expected_date: e.target.value })} data-testid="inst-expected" /></div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setInstDialog(null)}>Annulla</Button><Button onClick={saveInst} disabled={savingInst} data-testid="save-instance-btn">{savingInst ? 'Salvo...' : 'Salva'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ MANODOPERA ============ */
function LaborSection({ year, sites, employees, canManage, onChange, siteName, siteFilter }) {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const payslipRef = useRef(null);

  const load = useCallback(async () => { const r = await api.get('/finance/labor-costs', { params: { year, ...(siteFilter ? { site_id: siteFilter } : {}) } }); setItems(r.data || []); }, [year, siteFilter]);
  useEffect(() => { load(); }, [load]);

  const onPayslipFile = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/payslips/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(r.data); load(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Errore analisi buste paga'); }
    finally { setImporting(false); if (payslipRef.current) payslipRef.current.value = ''; }
  };

  const save = async () => {
    if (!dialog.amount) { alert('Importo obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...dialog, amount: Number(dialog.amount), gross_amount: dialog.gross_amount ? Number(dialog.gross_amount) : 0, site_id: dialog.site_id === '__none__' ? null : dialog.site_id, employee_id: dialog.employee_id === '__none__' ? null : dialog.employee_id };
      if (dialog.id) await api.put(`/finance/labor-costs/${dialog.id}`, payload);
      else await api.post('/finance/labor-costs', payload);
      setDialog(null); load(); onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };
  const del = async (i) => { if (!window.confirm('Eliminare il costo?')) return; await api.delete(`/finance/labor-costs/${i.id}`); load(); onChange(); };
  const total = useMemo(() => items.reduce((s, i) => s + (Number(i.amount) || 0), 0), [items]);
  const onPickEmployee = (id) => {
    const e = employees.find(x => x.id === id);
    setDialog(d => ({ ...d, employee_id: id, employee_name: e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : d.employee_name }));
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div><h3 className="text-sm font-semibold text-slate-700">Costi manodopera per cantiere</h3><p className="text-xs text-slate-400">Totale {year}: <span className="font-semibold text-slate-600">{eur(total)}</span></p></div>
          {canManage && (
            <div className="flex items-center gap-2">
              <input ref={payslipRef} type="file" accept=".pdf" className="hidden" onChange={(e) => onPayslipFile(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => payslipRef.current?.click()} disabled={importing} data-testid="import-payslip-btn"><Upload size={16} className="mr-1" /> {importing ? 'Analizzo...' : 'Importa buste paga (PDF)'}</Button>
              <Button onClick={() => setDialog(emptyLabor())} data-testid="new-labor-btn"><Plus size={16} className="mr-1" /> Aggiungi costo</Button>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><AlertTriangle size={13} /> Importa il PDF unico delle buste paga: leggo netto/lordo per dipendente (via Codice Fiscale) e li pre-carico qui. Poi assegna il cantiere a ciascuno.</p>
        {/* Mobile: lista a schede */}
        <div className="md:hidden space-y-2">
          {items.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessun costo registrato</p>}
          {items.map(i => (
            <div key={i.id} className="border border-slate-100 rounded-lg p-3" data-testid={`labor-card-${i.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{i.employee_name || '-'}</p>
                  <p className="text-xs text-slate-500">{i.month} · {i.site_name || <span className="text-amber-600">Da assegnare</span>}</p>
                  <Badge variant="outline" className="text-xs mt-1">{i.source === 'payslip' ? 'Busta paga' : i.source === 'attendance' ? 'Presenze' : 'Manuale'}</Badge>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold">{eur(i.amount)}</p>
                  {i.gross_amount ? <p className="text-xs text-slate-400">lordo {eur(i.gross_amount)}</p> : null}
                  {canManage && <div className="flex items-center gap-2 justify-end mt-1">
                    <button onClick={() => setDialog({ ...i, site_id: i.site_id || '__none__', employee_id: i.employee_id || '__none__', amount: String(i.amount), gross_amount: i.gross_amount ? String(i.gross_amount) : '' })} data-testid={`m-edit-labor-${i.id}`}><Pencil size={15} className="text-slate-500" /></button>
                    <button onClick={() => del(i)} data-testid={`m-del-labor-${i.id}`}><Trash2 size={15} className="text-red-500" /></button>
                  </div>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2 pr-2">Dipendente</th><th className="pr-2">Mese</th><th className="pr-2">Cantiere</th><th className="pr-2 text-right">Netto</th><th className="pr-2 text-right">Lordo</th><th className="pr-2">Fonte</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">Nessun costo registrato</td></tr>}
              {items.map(i => (
                <tr key={i.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`labor-row-${i.id}`}>
                  <td className="py-2 pr-2 font-medium text-slate-800">{i.employee_name || '-'}</td>
                  <td className="pr-2 text-slate-500">{i.month}</td>
                  <td className="pr-2 text-slate-500">{i.site_name || <span className="text-amber-600">Da assegnare</span>}</td>
                  <td className="pr-2 text-right font-semibold">{eur(i.amount)}</td>
                  <td className="pr-2 text-right text-slate-500">{i.gross_amount ? eur(i.gross_amount) : '-'}</td>
                  <td className="pr-2"><Badge variant="outline" className="text-xs">{i.source === 'payslip' ? 'Busta paga' : i.source === 'attendance' ? 'Presenze' : 'Manuale'}</Badge></td>
                  <td className="pr-1 whitespace-nowrap">{canManage && <><Button size="icon" variant="ghost" onClick={() => setDialog({ ...i, site_id: i.site_id || '__none__', employee_id: i.employee_id || '__none__', amount: String(i.amount), gross_amount: i.gross_amount ? String(i.gross_amount) : '' })} data-testid={`edit-labor-${i.id}`}><Pencil size={14} /></Button><Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(i)} data-testid={`del-labor-${i.id}`}><Trash2 size={14} /></Button></>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{dialog?.id ? 'Modifica' : 'Nuovo'} costo manodopera</DialogTitle></DialogHeader>
            {dialog && (
              <div className="space-y-3">
                <div className="space-y-1"><Label>Dipendente</Label>
                  <Select value={dialog.employee_id} onValueChange={onPickEmployee}><SelectTrigger data-testid="labor-employee"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent><SelectItem value="__none__">— (inserisci nome sotto)</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1"><Label>Nome (se non in elenco)</Label><Input value={dialog.employee_name} onChange={(e) => setDialog({ ...dialog, employee_name: e.target.value })} data-testid="labor-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Mese</Label><Input type="month" value={dialog.month} onChange={(e) => setDialog({ ...dialog, month: e.target.value })} data-testid="labor-month" /></div>
                  <div className="space-y-1"><Label>Cantiere</Label><Select value={dialog.site_id} onValueChange={(v) => setDialog({ ...dialog, site_id: v })}><SelectTrigger data-testid="labor-site"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Da assegnare</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Netto (€) *</Label><Input type="number" step="0.01" value={dialog.amount} onChange={(e) => setDialog({ ...dialog, amount: e.target.value })} data-testid="labor-amount" /></div>
                  <div className="space-y-1"><Label>Lordo (€, opz.)</Label><Input type="number" step="0.01" value={dialog.gross_amount} onChange={(e) => setDialog({ ...dialog, gross_amount: e.target.value })} data-testid="labor-gross" /></div>
                </div>
                <div className="space-y-1"><Label>Note</Label><Input value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} /></div>
              </div>
            )}
            <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button><Button onClick={save} disabled={saving} data-testid="save-labor-btn">{saving ? 'Salvo...' : 'Salva'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Buste paga analizzate</DialogTitle><DialogDescription>{importResult?.labor_created} costi manodopera pre-caricati su {importResult?.total_pages} cedolini. Assegna il cantiere a ciascuno dalla tabella.</DialogDescription></DialogHeader>
            <div className="border rounded divide-y max-h-72 overflow-y-auto text-sm">
              {importResult?.rows?.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-1.5">
                  <span className={r.matched ? 'text-slate-700' : 'text-amber-600'}>{r.employee_name || `CF ${r.codice_fiscale || '?'}`} <span className="text-slate-400">· {r.month}</span></span>
                  <span className="font-medium">{eur(r.netto)}{r.lordo ? <span className="text-slate-400 font-normal"> / {eur(r.lordo)} lordo</span> : ''}</span>
                </div>
              ))}
            </div>
            <DialogFooter><Button onClick={() => setImportResult(null)}>Chiudi</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
